import React from 'react'
import PropTypes from 'prop-types'
import { motion, useReducedMotion } from 'framer-motion'

/**
 * AnimateOnView
 * - triggers animations when element enters the viewport
 * - respects prefers-reduced-motion
 * - accepts custom variants and delay
 * - uses viewport={{ once: true }} by default
 *
 * Props:
 * - variants: framer-motion variants object (optional)
 * - delay: number (seconds) optional
 * - transition: custom transition object (optional)
 * - children, className, style, and other div props
 */
export default function AnimateOnView({
  children = null,
  variants = undefined,
  delay = 0,
  transition = undefined,
  className = undefined,
  style = undefined,
  ...rest
}) {
  const reduceMotion = useReducedMotion()

  const defaultVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  }

  // If user prefers reduced motion, skip transforms and show immediately
  const safeVariants = reduceMotion
    ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } }
    : variants || defaultVariants

  const safeTransition = reduceMotion
    ? { duration: 0 }
    : { duration: 0.6, ease: 'easeOut', delay, ...(transition || {}) }

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={safeVariants}
      transition={safeTransition}
      className={className}
      style={style}
      {...rest}
    >
      {children}
    </motion.div>
  )
}

// PropTypes validation (follow project conventions)
AnimateOnView.propTypes = {
  children: PropTypes.node,
  // variants is an object mapping variant names to definitions (use shape for lint)
  variants: PropTypes.shape({}),
  delay: PropTypes.number,
  // transition is a Framer Motion transition object; use shape({}) to satisfy lint rules
  transition: PropTypes.shape({}),
  className: PropTypes.string,
  // style is a plain style object: use shape({}) as a permissive validator
  style: PropTypes.shape({}),
}

// defaultProps to satisfy react/require-default-props lint rule
AnimateOnView.defaultProps = {
  children: null,
  variants: undefined,
  delay: 0,
  transition: undefined,
  className: undefined,
  style: undefined,
}


